import React from 'react';
import { createPortal } from 'react-dom';
import { NotificationResponse, NotificationPriority } from '@troia-v3/types';
import { useMarkAsRead, useArchiveNotifications } from '../hooks';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/shared/icons'
interface NotificationPanelProps {
  notifications: NotificationResponse[];
  unreadCount: number;
  onClose: () => void;
  /** Fixed viewport coordinates (computed by the parent from the trigger rect). */
  position: { left: number; top: number };
  /** Fixed panel width in px (must match the parent's panelWidth used for clamping). */
  panelWidth: number;
}

/**
 * Notification dropdown panel
 * Shows recent notifications with actions.
 * Portaled to document.body with position:fixed so it escapes the sidebar's
 * `transform` stacking context and the MainLayout `overflow-hidden` clip.
 * Opens upward, anchored to the right of the sidebar trigger.
 */
export const NotificationPanel = React.forwardRef<HTMLDivElement, NotificationPanelProps>(({
  notifications,
  unreadCount,
  onClose,
  position,
  panelWidth
}, ref) => {
  const navigate = useNavigate();
  const markAsReadMutation = useMarkAsRead();
  const archiveMutation = useArchiveNotifications();

  const handleMarkAsRead = (notificationId: string) => {
    markAsReadMutation.mutate([notificationId]);
  };

  const handleMarkAllAsRead = () => {
    const unreadIds = notifications
      .filter(n => !n.read)
      .map(n => n.id);

    if (unreadIds.length > 0) {
      markAsReadMutation.mutate(unreadIds);
    }
  };

  const handleArchive = (notificationId: string) => {
    archiveMutation.mutate([notificationId]);
  };

  const handleActionClick = (notification: NotificationResponse) => {
    if (notification.actionUrl) {
      // Mark as read when clicking action
      if (!notification.read) {
        handleMarkAsRead(notification.id);
      }
      // Navigate to action URL
      navigate(notification.actionUrl);
      // Close dropdown
      onClose();
    }
  };

  const getPriorityColor = (priority: NotificationPriority): string => {
    switch (priority) {
      case 'urgent': return '#ef4444'; // red-500
      case 'high': return '#f97316'; // orange-500
      case 'normal': return '#3b82f6'; // blue-500
      case 'low': return '#6b7280'; // gray-500
      default: return '#6b7280';
    }
  };

  return createPortal(
    (
      /* Panel — portaled to body, fixed, opens upward/right (positioned by parent).
         Outside-click is handled by the parent's portal-aware document listener. */
      <div
        ref={ref}
        className="rounded-lg shadow-xl"
        style={{
          position: 'fixed',
          left: position.left,
          top: position.top,
          width: panelWidth,
          zIndex: 1000,
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          maxHeight: '600px'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-2">
            <h3
              className="font-semibold text-sm"
              style={{ color: '#1e293b' }}
            >
              Notificações
            </h3>
            {unreadCount > 0 && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full"
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff'
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="p-1.5 rounded transition-colors"
                style={{ color: '#64748b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }}
                title="Marcar todas como lidas"
                data-testid="notification-panel-mark-all"
              >
                <Icon name="check-circle" className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={() => { navigate('/settings/notifications'); onClose(); }}
              className="p-1.5 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
              title="Configurações de notificação"
              data-testid="notification-panel-settings"
            >
              <Icon name="settings" className="w-4 h-4" />
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
              data-testid="notification-panel-close"
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: '500px' }}
        >
          {notifications.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <p className="text-sm" style={{ color: '#64748b' }}>
                Nenhuma notificação
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className="border-b transition-colors"
                style={{
                  backgroundColor: notification.read ? 'transparent' : '#f9fafb',
                  borderColor: '#e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = notification.read
                    ? 'transparent'
                    : '#f9fafb';
                }}
              >
                <div className="px-4 py-3">
                  {/* Title and Priority */}
                  <div className="flex items-start gap-2 mb-1">
                    {/* Priority indicator */}
                    <div
                      className="w-1 h-1 rounded-full mt-2 flex-shrink-0"
                      style={{
                        backgroundColor: getPriorityColor(notification.priority),
                        width: '6px',
                        height: '6px'
                      }}
                    />

                    <div className="flex-1 min-w-0">
                      <h4
                        className="text-sm font-medium"
                        style={{ color: '#1e293b' }}
                      >
                        {notification.title}
                      </h4>

                      <p
                        className="text-sm mt-0.5"
                        style={{ color: '#64748b' }}
                      >
                        {notification.message}
                      </p>

                      {/* Action Button */}
                      {notification.actionUrl && notification.actionLabel && (
                        <button
                          onClick={() => handleActionClick(notification)}
                          className="flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                          style={{
                            backgroundColor: '#e0e7ff',
                            color: '#4f46e5'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#c7d2fe';
                            e.currentTarget.style.color = '#4338ca';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#e0e7ff';
                            e.currentTarget.style.color = '#4f46e5';
                          }}
                        >
                          <Icon name="external-link" className="w-3 h-3" />
                          {notification.actionLabel}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Footer: Timestamp and Actions */}
                  <div className="flex items-center justify-between mt-2">
                    <span
                      className="text-xs"
                      style={{ color: '#9ca3af' }}
                    >
                      {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                        locale: ptBR
                      })}
                    </span>

                    <div className="flex items-center gap-1">
                      {!notification.read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-1 rounded transition-colors"
                          style={{ color: '#64748b' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e0e7ff';
                            e.currentTarget.style.color = '#4f46e5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#64748b';
                          }}
                          title="Marcar como lida"
                        >
                          <Icon name="check" className="w-3.5 h-3.5" />
                        </button>
                      )}

                      <button
                        onClick={() => handleArchive(notification.id)}
                        className="p-1 rounded transition-colors"
                        style={{ color: '#64748b' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#64748b';
                        }}
                        title="Arquivar"
                      >
                        <Icon name="archive" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div
            className="px-4 py-3 border-t text-center"
            style={{
              backgroundColor: '#f9fafb',
              borderColor: '#e5e7eb'
            }}
          >
            <button
              onClick={() => { navigate('/notifications'); onClose(); }}
              className="text-sm font-medium transition-colors"
              style={{ color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#5b21b6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6d28d9';
              }}
              data-testid="notification-panel-view-all"
            >
              Ver todas as notificações
            </button>
          </div>
        )}
      </div>
    ),
    document.body
  );
});

NotificationPanel.displayName = 'NotificationPanel';
